import React, { ReactNode } from 'react'
import gql from 'graphql-tag'
import memoize from 'lodash.memoize'
import { Query } from 'react-apollo'
import {
  vulnerabilityOrSinkGroupFragment,
  VulnerabilityApplication,
  VulnerabilityOrSinkGroup
} from './shared'
import { AgentLanguage } from '../agent-language'
import { GQLVulnerabilityOrSinkGroup } from '../../graphql/schema/types/vulnerability-sink-group'
import {
  GQLVulnerabilityApplication,
  GQLVulnerability
} from '../../graphql/schema/types/vulnerability'
import { GQLBugTracker } from '../../graphql/schema/types/bug-trackers'
import { BugTracker } from '../bug-tracker/index'

export const vulnerabilityOrSinkGroupQuery = gql`
  query GetVulnerabilityOrSinkGroup(
    $organizationUuid: ID!
    $vulnerabilityOrSinkGroupId: String!
  ) {
    vulnerabilityOrSinkGroup(
      organizationUuid: $organizationUuid
      vulnerabilityOrSinkGroupId: $vulnerabilityOrSinkGroupId
    ) {
      ...VulnerabilityOrSinkGroup
    }
  }

  ${vulnerabilityOrSinkGroupFragment}
`
interface VulnerabilityOrSinkGroupQueryProps {
  readonly organizationUuid: string
  readonly rowDataID: string
  readonly children: (rowData: VulnerabilityOrSinkGroup) => ReactNode
}

export const getVulnerabilityOrSinkGroup = memoize(
  (organizationUuid) =>
    ({ rowDataID, children }) =>
      VulnerabilityOrSinkGroupRowQuery({
        organizationUuid,
        rowDataID,
        children
      })
)

export function VulnerabilityOrSinkGroupRowQuery({
  organizationUuid,
  rowDataID: vulnerabilityOrSinkGroupId,
  children
}: VulnerabilityOrSinkGroupQueryProps) {
  return (
    <Query
      query={vulnerabilityOrSinkGroupQuery}
      fetchPolicy="cache-only"
      variables={{
        vulnerabilityOrSinkGroupId,
        organizationUuid
      }}
    >
      {({ data }) => {
        const { vulnerabilityOrSinkGroup } = data
        return children(
          normalizeGQLVulnerabilityOrSinkGroup(vulnerabilityOrSinkGroup)
        )
      }}
    </Query>
  )
}

function normalizeGQLVulnerabilityOrSinkGroup(
  vulnerabilityOrSinkGroup: GQLVulnerabilityOrSinkGroup
): VulnerabilityOrSinkGroup {
  const { id, sinkGroup, vulnerability } = vulnerabilityOrSinkGroup

  if (sinkGroup) {
    return {
      id,
      sinkGroup: {
        ...sinkGroup,
        application: sinkGroup.application
          ? normalizeGQLApplication(sinkGroup.application)
          : undefined,
        substatus: sinkGroup.substatus || undefined,
        protectStatus: sinkGroup.protectStatus || undefined
      },
      vulnerability: undefined
    }
  } else if (vulnerability) {
    return {
      id,
      vulnerability: normalizeVulnerability(vulnerability),
      sinkGroup: undefined
    }
  }
  // Error because there should always be a sink group or vulnerability in the API response
  throw new Error('No vulnerability or sink group found from cache')
}

function normalizeVulnerability(vulnerability: GQLVulnerability) {
  return {
    ...vulnerability,
    application: normalizeGQLApplication(vulnerability.application),
    bugTrackers: normalizeGQLBugTrackers(vulnerability.bugTrackers),
    substatus: vulnerability.substatus || undefined,
    reportedToBugTrackerTime:
      vulnerability.reportedToBugTrackerTime || undefined,
    pendingStatus: vulnerability.pendingStatus || undefined
  }
}

function normalizeGQLApplication(
  application: GQLVulnerabilityApplication
): VulnerabilityApplication {
  return {
    ...application,
    language: AgentLanguage.fromValue(application.language)
  }
}

function normalizeGQLBugTrackers(bugtrackers: GQLBugTracker[]): BugTracker[] {
  return bugtrackers.map((tracker) => {
    return { ...tracker, ticketUrl: tracker.ticketUrl || undefined }
  })
}
